import { useState } from "react";
import { useParams } from "react-router-dom";
import {
  Badge,
  Card,
  CardBody,
  CardHeader,
  Field,
  Select,
  StudioState,
  TextArea,
  TextInput,
} from "@odingard/design-system";
import { SOP_STEP_RESULTS, type SopRunOutcome, type SopStepResult } from "@odingard/sdk-client";
import { useAsync } from "../../lib/useAsync";
import { useAuth } from "../../lib/auth";
import { sopApi } from "../../lib/api";
import { GovernedAction } from "../../governance/GovernedAction";

export function SopRunPage() {
  const { activeOrg } = useAuth();
  const orgId = activeOrg?.id ?? "";
  const { runId = "" } = useParams();
  const [result, setResult] = useState<SopStepResult>("DONE");
  const [notes, setNotes] = useState("");
  const [documentId, setDocumentId] = useState("");
  const [whatWasDone, setWhatWasDone] = useState("");
  const [why, setWhy] = useState("");
  const [deviationStepId, setDeviationStepId] = useState("");
  const [outcome, setOutcome] = useState<SopRunOutcome>("PASSED");

  const state = useAsync(async () => {
    const run = (await sopApi.run(orgId, runId)).data.run;
    const [version, records, evidence, deviations] = await Promise.all([
      sopApi.version(orgId, run.procedureId, run.versionId),
      sopApi.runSteps(orgId, runId),
      sopApi.evidence(orgId, runId),
      sopApi.deviations(orgId, runId),
    ]);
    return {
      run,
      steps: version.data.version.steps ?? [],
      records: records.data.steps,
      evidence: evidence.data.evidence,
      deviations: deviations.data.deviations,
    };
  }, [orgId, runId]);

  if (state.loading) return <StudioState state="loading" description="Loading the run…" />;
  if (state.error)
    return <StudioState state="error" description={state.error.message} onRetry={state.reload} />;
  if (!state.data) return null;
  const { run, steps, records, evidence, deviations } = state.data;
  const open = run.state === "IN_PROGRESS" || run.state === "BLOCKED";

  return (
    <>
      <div className="page-head">
        <div>
          <h1>Run of {run.subjectReference ?? run.procedureId}</h1>
          <p>
            Started {run.startedAt ?? "recently"} by {run.performerId ?? "an unknown performer"}.
          </p>
        </div>
        <div className="ods-actions">
          <Badge tone={run.state === "COMPLETED" ? "success" : "warning"}>{run.state}</Badge>
          {run.outcome && <Badge tone="neutral">{run.outcome}</Badge>}
          {open && (
            <GovernedAction
              permission="procedures.execute"
              label="Raise a deviation"
              title="Raise a deviation"
              description="Record a departure from the procedure as it is written. An approver decides whether it is accepted."
              confirmLabel="Raise deviation"
              onConfirm={() =>
                sopApi
                  .recordDeviation(orgId, run.id, {
                    version: run.version,
                    whatWasDone: whatWasDone.trim(),
                    why: why.trim(),
                    ...(deviationStepId ? { stepId: deviationStepId } : {}),
                  })
                  .then((raised) => {
                    setWhatWasDone("");
                    setWhy("");
                    return raised;
                  })
              }
              onDone={state.reload}
            >
              <Field label="Step" help="Leave unset if the deviation concerns the whole run.">
                {(id) => (
                  <Select
                    id={id}
                    value={deviationStepId}
                    onChange={(event) => setDeviationStepId(event.target.value)}
                  >
                    <option value="">The whole run</option>
                    {steps.map((step) => (
                      <option key={step.id} value={step.id}>
                        {step.stepOrder}. {step.instruction}
                      </option>
                    ))}
                  </Select>
                )}
              </Field>
              <Field label="What was done instead">
                {(id) => (
                  <TextArea
                    id={id}
                    rows={3}
                    value={whatWasDone}
                    onChange={(event) => setWhatWasDone(event.target.value)}
                  />
                )}
              </Field>
              <Field label="Why">
                {(id) => (
                  <TextArea id={id} rows={3} value={why} onChange={(event) => setWhy(event.target.value)} />
                )}
              </Field>
              {(!whatWasDone.trim() || !why.trim()) && (
                <p className="ods-denial">Both what was done and why are required.</p>
              )}
            </GovernedAction>
          )}
          {open && (
            <GovernedAction
              permission="procedures.execute"
              label="Close the run"
              title="Close this run"
              consequence="A closed run cannot be reopened; a further run is a new run."
              confirmLabel="Close run"
              onConfirm={() => sopApi.closeRun(orgId, run.id, outcome, run.version)}
              onDone={state.reload}
            >
              <Field label="Outcome">
                {(id) => (
                  <Select
                    id={id}
                    value={outcome}
                    onChange={(event) => setOutcome(event.target.value as SopRunOutcome)}
                  >
                    <option value="PASSED">PASSED</option>
                    <option value="PASSED_WITH_DEVIATION">PASSED_WITH_DEVIATION</option>
                    <option value="FAILED">FAILED</option>
                  </Select>
                )}
              </Field>
            </GovernedAction>
          )}
        </div>
      </div>

      <div className="ods-stack">
        <Card>
          <CardHeader title="Steps" />
          <CardBody>
            {steps.length === 0 ? (
              <StudioState
                state="empty"
                title="No steps"
                description="The version this run performs declares no steps."
              />
            ) : (
              <ol className="ods-stack">
                {steps.map((step) => {
                  const record = records.find((candidate) => candidate.stepId === step.id);
                  return (
                    <li key={step.id} className="ods-card ods-card--inset">
                      <strong>
                        {step.stepOrder}. {step.instruction}
                      </strong>
                      <div className="ods-actions">
                        {record ? (
                          <Badge tone={record.result === "DONE" ? "success" : "warning"}>
                            {record.result}
                          </Badge>
                        ) : (
                          <Badge tone="neutral">Not performed</Badge>
                        )}
                        {record?.verifiedAt && <Badge tone="success">Verified</Badge>}
                        {step.requiresVerification && !record?.verifiedAt && (
                          <Badge tone="info">Second person required</Badge>
                        )}
                      </div>
                      {record?.notes ? <p>{record.notes}</p> : null}
                      <div className="ods-actions">
                        {open && !record && (
                          <GovernedAction
                            permission="procedures.execute"
                            label="Record this step"
                            title={`Record step ${String(step.stepOrder)}`}
                            description={step.instruction}
                            confirmLabel="Record"
                            onConfirm={() =>
                              sopApi
                                .recordStep(orgId, run.id, {
                                  stepId: step.id,
                                  version: run.version,
                                  result,
                                  notes: notes.trim(),
                                })
                                .then((recorded) => {
                                  setNotes("");
                                  return recorded;
                                })
                            }
                            onDone={state.reload}
                          >
                            <Field label="Result">
                              {(id) => (
                                <Select
                                  id={id}
                                  value={result}
                                  onChange={(event) => setResult(event.target.value as SopStepResult)}
                                >
                                  {SOP_STEP_RESULTS.map((value) => (
                                    <option key={value} value={value}>
                                      {value}
                                    </option>
                                  ))}
                                </Select>
                              )}
                            </Field>
                            <Field label="Notes">
                              {(id) => (
                                <TextArea
                                  id={id}
                                  rows={3}
                                  value={notes}
                                  onChange={(event) => setNotes(event.target.value)}
                                />
                              )}
                            </Field>
                          </GovernedAction>
                        )}
                        {record && step.requiresVerification && !record.verifiedAt && (
                          <GovernedAction
                            permission="procedures.verify"
                            label="Verify"
                            title={`Verify step ${String(step.stepOrder)}`}
                            description="Verification is a second person's statement that this step was performed as written."
                            confirmLabel="Verify"
                            onConfirm={() => sopApi.verifyStep(orgId, run.id, step.id, run.version)}
                            onDone={state.reload}
                          />
                        )}
                        {open && step.requiresEvidence && (
                          <GovernedAction
                            permission="procedures.execute"
                            label="Attach evidence"
                            title={`Attach evidence to step ${String(step.stepOrder)}`}
                            description="Evidence is a document the shared Documents platform already holds."
                            confirmLabel="Attach"
                            onConfirm={() =>
                              sopApi
                                .attachEvidence(orgId, run.id, {
                                  documentId: documentId.trim(),
                                  version: run.version,
                                  stepId: step.id,
                                })
                                .then((attached) => {
                                  setDocumentId("");
                                  return attached;
                                })
                            }
                            onDone={state.reload}
                          >
                            <Field label="Document identifier">
                              {(id) => (
                                <TextInput
                                  id={id}
                                  value={documentId}
                                  onChange={(event) => setDocumentId(event.target.value)}
                                />
                              )}
                            </Field>
                          </GovernedAction>
                        )}
                      </div>
                    </li>
                  );
                })}
              </ol>
            )}
          </CardBody>
        </Card>

        <Card>
          <CardHeader title="Deviations" />
          <CardBody>
            {deviations.length === 0 ? (
              <StudioState
                state="empty"
                title="No deviations"
                description="The run has followed the procedure as written."
              />
            ) : (
              <ul className="ods-stack">
                {deviations.map((deviation) => (
                  <li key={deviation.id} className="ods-card ods-card--inset">
                    <strong>{deviation.whatWasDone ?? "Deviation"}</strong>
                    <p>{deviation.why ?? "No reason recorded."}</p>
                    <div className="ods-actions">
                      <Badge tone={deviation.disposition ? "success" : "warning"}>
                        {deviation.disposition ?? "Awaiting a decision"}
                      </Badge>
                      {!deviation.disposition && (
                        <>
                          <GovernedAction
                            permission="procedures.approve"
                            label="Accept"
                            title="Accept this deviation"
                            confirmLabel="Accept"
                            requireRationale
                            onConfirm={(rationale) =>
                              sopApi.disposeDeviation(orgId, run.id, deviation.id, "ACCEPTED", rationale)
                            }
                            onDone={state.reload}
                          />
                          <GovernedAction
                            permission="procedures.approve"
                            label="Reject"
                            title="Reject this deviation"
                            destructive
                            confirmLabel="Reject"
                            requireRationale
                            onConfirm={(rationale) =>
                              sopApi.disposeDeviation(orgId, run.id, deviation.id, "REJECTED", rationale)
                            }
                            onDone={state.reload}
                          />
                        </>
                      )}
                    </div>
                    {deviation.dispositionRationale ? <p>{deviation.dispositionRationale}</p> : null}
                  </li>
                ))}
              </ul>
            )}
          </CardBody>
        </Card>

        <Card>
          <CardHeader title="Evidence" />
          <CardBody>
            {evidence.length === 0 ? (
              <StudioState
                state="empty"
                title="No evidence yet"
                description="Attach a document to a step that requires evidence."
              />
            ) : (
              <ul className="ods-stack">
                {evidence.map((item) => (
                  <li key={item.id}>
                    <strong>{item.documentId}</strong>
                    <p>{item.note?.trim() ? item.note : "No note recorded."}</p>
                  </li>
                ))}
              </ul>
            )}
          </CardBody>
        </Card>
      </div>
    </>
  );
}
